import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { authService } from '../services/authService';
import { ADMIN_REFRESH_KEY, IMPERSONATED_KEY, REFRESH_KEY, leggi as leggiRefresh } from '../services/sessione';

/**
 * Impersonazione cliente — flusso admin/agente.
 *
 * Quando un admin avvia l'impersonazione di un cliente:
 *  1) Salviamo i token correnti dell'admin in sessionStorage
 *     (``adminAccessToken`` + ``adminRefreshToken``) come "ritorno".
 *  2) Chiediamo a ``POST /api/auth/impersonate/{userId}`` un nuovo
 *     ``TokenPair`` emesso a nome del cliente.
 *  3) Applichiamo i nuovi token all'``AuthContext`` (``applyTokens``).
 *     Il profilo ``/me`` viene rifetchato → l'utente loggato diventa
 *     il cliente impersonato → il catalogo, gli sconti, il budget e
 *     il carrello vengono mostrati con le sue condizioni.
 *  4) Persistiamo lo stato ``impersonatedClient`` in sessionStorage
 *     così sopravvive ai reload.
 *
 * ``stopImpersonation`` esegue il flusso inverso. L'audit log è stato
 * volutamente rimosso (2026-02, su richiesta utente).
 */

interface ImpersonatedClient {
  /** Codice anagrafica NTS (chiave primaria nel portale). */
  anConto: number;
  /** Ragione sociale visualizzata nel banner. */
  name: string;
}

interface AdminContextValue {
  isAdmin: boolean;
  impersonatedClient: ImpersonatedClient | null;
  /** True mentre lo swap token è in corso. */
  isImpersonating: boolean;
  /**
   * Avvia l'impersonazione di un'anagrafica NTS (qualunque cliente,
   * anche senza account web). Swappa i token admin → cliente.
   * In caso di errore (es. 404) ripristina i token admin.
   */
  startImpersonation: (anConto: number, name: string) => Promise<void>;
  /** Termina l'impersonazione e ripristina i token admin. */
  stopImpersonation: () => Promise<void>;
  /**
   * Ottiene un access token ADMIN valido per chiamate ad endpoint
   * admin-only mentre l'impersonazione è attiva (es. lista clienti
   * nella pagina "Cambia cliente"). L'istanza axios condivisa usa
   * sempre il token ATTIVO (quello del cliente impersonato durante
   * un'impersonazione) — per le chiamate che DEVONO restare
   * admin-scoped si passa questo token come header override.
   * Ritorna ``null`` se non c'è un'impersonazione in corso (in tal
   * caso il token attivo è già quello admin, nessun override serve).
   */
  getAdminToken: () => Promise<string | null>;
}

const AdminContext = createContext<AdminContextValue | null>(null);

const ADMIN_ACCESS_KEY = 'dtk_admin_access_token';

/** Chiude sul server la sessione dell'impersonazione in corso (best effort:
 *  si chiude comunque con quella dell'operatore). */
function chiudiImpersonazione(): void {
  const token = leggiRefresh();
  if (token) authService.logout(token).catch(() => {});
}

export function AdminProvider({ children }: { children: React.ReactNode }) {
  const { user, applyTokens } = useAuth();
  // ``isAdmin`` non basta perché durante l'impersonazione l'utente
  // attivo è il cliente — quindi controlliamo anche se esistono token
  // di backup salvati. In caso affermativo il pannello admin resta
  // accessibile (manteniamo i privilegi di "ritorno"). NB: il backup
  // salvato da ``startImpersonation`` è il REFRESH token (non l'access,
  // mai scritto) — controllare ``ADMIN_ACCESS_KEY`` qui era un bug che
  // faceva risultare ``hasAdminBackup`` sempre false durante
  // l'impersonazione (bottone "Cambia" del banner non funzionante).
  const hasAdminBackup = typeof window !== 'undefined' && !!sessionStorage.getItem(ADMIN_REFRESH_KEY);
  const isAdmin = user?.ruolo === 'admin' || hasAdminBackup;

  const [impersonatedClient, setImpersonatedClient] = useState<ImpersonatedClient | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      const raw = sessionStorage.getItem(IMPERSONATED_KEY);
      return raw ? (JSON.parse(raw) as ImpersonatedClient) : null;
    } catch {
      return null;
    }
  });
  const [isImpersonating, setIsImpersonating] = useState(false);

  // Sync sessionStorage ↔ state
  useEffect(() => {
    if (impersonatedClient) {
      sessionStorage.setItem(IMPERSONATED_KEY, JSON.stringify(impersonatedClient));
    } else {
      sessionStorage.removeItem(IMPERSONATED_KEY);
    }
  }, [impersonatedClient]);

  const startImpersonation = useCallback(
    async (anConto: number, name: string) => {
      // Se un'impersonazione è già in corso (swap "Cambia" senza prima
      // terminare), il token ATTIVO appartiene al cliente corrente —
      // sia il backup admin da conservare, sia l'autenticazione della
      // chiamata /impersonate/anagrafica (che richiede ruolo admin/agente
      // lato backend) devono usare il refresh token admin già in backup,
      // rigenerando un access token admin fresco al volo.
      const existingBackup = sessionStorage.getItem(ADMIN_REFRESH_KEY);
      let adminAccessTokenOverride: string | undefined;
      let adminRefreshToBackup: string | null;

      if (existingBackup) {
        const refreshed = await authService.refresh(existingBackup);
        adminAccessTokenOverride = refreshed.data.access_token;
        adminRefreshToBackup = refreshed.data.refresh_token; // rotazione
      } else {
        // Prima impersonazione: il token attivo è già quello admin reale,
        // nessun override necessario per la chiamata.
        adminRefreshToBackup = sessionStorage.getItem(REFRESH_KEY);
      }
      if (!adminRefreshToBackup) {
        throw new Error('Sessione admin scaduta. Effettua nuovamente il login.');
      }

      setIsImpersonating(true);
      try {
        const res = await authService.impersonateByAnConto(anConto, adminAccessTokenOverride);
        // "Cambia" cliente: l'impersonazione precedente finisce qui.
        if (existingBackup) chiudiImpersonazione();
        sessionStorage.setItem(ADMIN_REFRESH_KEY, adminRefreshToBackup);
        await applyTokens(res.data.access_token, res.data.refresh_token);
        setImpersonatedClient({ anConto, name });
      } catch (e) {
        if (!existingBackup) sessionStorage.removeItem(ADMIN_REFRESH_KEY);
        throw e;
      } finally {
        setIsImpersonating(false);
      }
    },
    [applyTokens],
  );

  const stopImpersonation = useCallback(async () => {
    const adminRefresh = sessionStorage.getItem(ADMIN_REFRESH_KEY);
    if (!adminRefresh) {
      // Nessun backup → fail-safe: cancella stato locale senza swap
      setImpersonatedClient(null);
      return;
    }
    setIsImpersonating(true);
    try {
      // Rigeneriamo un access token admin via refresh
      const refreshed = await authService.refresh(adminRefresh);
      chiudiImpersonazione();
      await applyTokens(refreshed.data.access_token, refreshed.data.refresh_token);
    } finally {
      sessionStorage.removeItem(ADMIN_REFRESH_KEY);
      sessionStorage.removeItem(ADMIN_ACCESS_KEY); // legacy cleanup
      setImpersonatedClient(null);
      setIsImpersonating(false);
    }
  }, [applyTokens]);

  const getAdminToken = useCallback(async (): Promise<string | null> => {
    const adminRefresh = sessionStorage.getItem(ADMIN_REFRESH_KEY);
    if (!adminRefresh) return null;
    const refreshed = await authService.refresh(adminRefresh);
    // Rotazione: il backend emette un nuovo refresh token ad ogni
    // refresh — sostituiamo quello di backup per restare valido.
    sessionStorage.setItem(ADMIN_REFRESH_KEY, refreshed.data.refresh_token);
    return refreshed.data.access_token;
  }, []);

  return (
    <AdminContext.Provider
      value={{ isAdmin, impersonatedClient, isImpersonating, startImpersonation, stopImpersonation, getAdminToken }}
    >
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin(): AdminContextValue {
  const ctx = useContext(AdminContext);
  if (!ctx) throw new Error('useAdmin must be used within AdminProvider');
  return ctx;
}
